import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { isDesktopBreakpoint } from '@theme/utilities';
import { PrefetchCache } from '@theme/section-renderer';

class BackInStockComponent extends Component {
  static BACK_IN_STOCK_SECTION_ID = 'section-rendering-back-in-stock-dialog';
  static HOVER_DELAY = 150;
  static MAX_CACHE_SIZE = 10;
  /** @type {PrefetchCache<Element>} */
  #cache = new PrefetchCache({
    fetcher: (url, signal) => this.#fetchDialog(url, signal),
    maxSize: BackInStockComponent.MAX_CACHE_SIZE,
    shouldCache: () => !window.Shopify?.designMode,
  });
  /** @type {number | null} */
  #hoverTimeout = null;
  /** @type {boolean} */
  #isClickFetching = false;
  /** @type {Element | null} - Section/dialog where variantUpdate listener is attached */
  #variantUpdateListenerTarget = null;

  #getRequestUrl() {
    const productUrl = this.dataset.productUrl;
    if (!productUrl) return null;
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('section_id', BackInStockComponent.BACK_IN_STOCK_SECTION_ID);
    return url.toString();
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.#handleHover);
    this.addEventListener('focusin', this.#handleHover);
    this.addEventListener('pointerleave', this.#handleHoverOut);
    this.addEventListener('focusout', this.#handleHoverOut);
    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection) {
      this.#variantUpdateListenerTarget = closestSection;
      closestSection.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerenter', this.#handleHover);
    this.removeEventListener('focusin', this.#handleHover);
    this.removeEventListener('pointerleave', this.#handleHoverOut);
    this.removeEventListener('focusout', this.#handleHoverOut);
    this.#clearHoverTimeout();
    this.#cache.abort();
    if (this.#variantUpdateListenerTarget) {
      this.#variantUpdateListenerTarget.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
      this.#variantUpdateListenerTarget = null;
    }
    this.#cache.clear();
  }

  /**
   * Updates data-product-url when variant changes
   * @param {Event} event - VariantUpdateEvent
   */
  #handleVariantUpdate = (event) => {
    const pickerContainer = event.target?.closest?.('.product-information');
    if (pickerContainer && !pickerContainer.contains(this)) return;

    if (event.detail?.data?.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
      // Switching products invalidates the cached dialogs (keyed by the old URL).
      this.#cache.clear();
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }
    const html = event.detail?.data?.html;
    if (!html) return;

    const blockId = this.dataset.blockId;
    let newBackInStock = blockId
      ? html.querySelector(`back-in-stock-component[data-block-id="${blockId}"]`)
      : null;
    if (!newBackInStock) return;
    morph(this, newBackInStock, { childrenOnly: false });
  };

  #handleHover = () => {
    if (window.Shopify?.designMode || !isDesktopBreakpoint()) return;
    const requestUrl = this.#getRequestUrl();
    if (!requestUrl) return;

    this.#clearHoverTimeout();
    this.#hoverTimeout = setTimeout(() => {
      if (this.#isClickFetching) return;
      this.#cache.prefetch(requestUrl);
    }, BackInStockComponent.HOVER_DELAY);
  };

  #handleHoverOut = () => {
    this.#clearHoverTimeout();
  };

  #clearHoverTimeout() {
    if (this.#hoverTimeout) {
      clearTimeout(this.#hoverTimeout);
      this.#hoverTimeout = null;
    }
  }

  /**
   * Injects dialog into DOM and opens it.
   * @param {Element} dialogEl - Cached dialog element
   * @param {string} dialogId - ID of the dialog element
   */
  #injectDialogAndOpen(dialogEl, dialogId) {
    const freshDialog = dialogEl.cloneNode(true);

    const existingDialog = document.getElementById(dialogId);
    if (existingDialog) {
      morph(existingDialog, freshDialog);
    } else {
      document.body.appendChild(freshDialog);
    }

    requestAnimationFrame(() => {
      const dialog = document.getElementById(dialogId);
      if (dialog?.openDialog) dialog.openDialog();
    });
  }

  /**
   * Fetcher for the prefetch cache: fetch the section and return a clone of the
   * dialog element. Aborts are surfaced as `null`.
   * @param {string} requestUrl - Full URL with section_id
   * @param {AbortSignal} signal - Abort signal from the cache
   * @returns {Promise<Element | null>}
   */
  async #fetchDialog(requestUrl, signal) {
    try {
      const response = await fetch(requestUrl, { signal });
      if (!response.ok) return null;

      const sectionHTML = await response.text();
      if (!sectionHTML) return null;

      const doc = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const newDialog = doc.querySelector('back-in-stock-dialog');
      if (!newDialog) return null;

      return /** @type {Element} */ (newDialog.cloneNode(true));
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return null;
      }
      throw error;
    }
  }

  /**
   * Handles back-in-stock button click.
   * Uses cache from hover prefetch when available, else fetches via Section Rendering API.
   * @param {Event} event - Click event
   */
  handleClick = async (event = null) => {
    if (event) {
      event.preventDefault();
    }
    const productUrl = this.dataset.productUrl;
    const dialogId = this.dataset.dialogId;
    if (!productUrl || !dialogId) return;

    const button = event?.target?.closest?.('.btn-notify-me');
    button?.classList.add('btn--loading');

    const requestUrl = this.#getRequestUrl();
    if (!requestUrl) return;

    try {
      // Cancel any hover prefetch and resolve via the shared cache
      // (cache hit → in-flight prefetch → fresh fetch).
      this.#clearHoverTimeout();
      this.#isClickFetching = true;
      let dialogEl;
      try {
        dialogEl = await this.#cache.get(requestUrl);
      } finally {
        this.#isClickFetching = false;
      }

      if (!dialogEl) return;

      this.#injectDialogAndOpen(dialogEl, dialogId);
    } catch (error) {
      console.error('Failed to load back-in-stock dialog', error);
    } finally {
      button?.classList.remove('btn--loading');
    }
  };
}

if (!customElements.get('back-in-stock-component')) {
  customElements.define('back-in-stock-component', BackInStockComponent);
}

/**
 * Back-in-stock dialog component.
 * Extends DialogComponent with auto-open when form has been submitted
 * (success or error message) - mirrors concept theme BackInStockDrawer behavior.
 */
class BackInStockDialogComponent extends DialogComponent {
  /** @type {number | null} */
  #openTimer = null;

  connectedCallback() {
    super.connectedCallback();

    // Auto-open when form has success or error message (after page reload from form submit)
    if (this.#hasSubmittedForm()) {
      this.#openTimer = setTimeout(() => {
        this.#openTimer = null;
        this.openDialog();
      }, 1000);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#openTimer) {
      clearTimeout(this.#openTimer);
      this.#openTimer = null;
    }
  }

  /**
   * Check if the form has been submitted (contains success or error message).
   * @returns {boolean}
   */
  #hasSubmittedForm() {
    return this.querySelector('.form__success, .form__error') !== null;
  }
}

if (!customElements.get('back-in-stock-dialog')) {
  customElements.define('back-in-stock-dialog', BackInStockDialogComponent);
}
